import { PurseDistribution } from '@/queries/program_purses/schemas'
import { Button } from '@/app/common/components/button/Button'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import { Input } from '@/app/common/components/input/Input'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { parseRequestError } from '@/app/common/utilities'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useOutletContext } from 'react-router-dom'
import { z } from 'zod'
import { PurseDetailOutletContext } from '../purse-detail-view'
import { useDownloadDistributionReport } from '@/queries/program_purses/useDownloadDistributionReport'

// Password validation schema based on validator schema in the backend.
// ProgramPurseController.php -> downloadPurseDistributionSummary
const PasswordSchema = z
  .string()
  .min(1, { message: 'Password is required' })
  .min(8, { message: 'Password must be at least 8 characters long' })
  .max(32, { message: 'Password must be at most 32 characters long' })
  .regex(/[a-z]/, {
    message: 'Password must contain at least one lowercase letter',
  })
  .regex(/[A-Z]/, {
    message: 'Password must contain at least one uppercase letter',
  })
  .regex(/[0-9]/, {
    message: 'Password must contain at least one number',
  })

const PasswordFormSchema = z.object({
  password: PasswordSchema,
})

type PasswordFormData = z.infer<typeof PasswordFormSchema>

type Props = {
  onOpenChange: (open: boolean) => void
  distribution: PurseDistribution
}

export default function EnterPasswordDialog({
  onOpenChange,
  distribution,
}: Props) {
  const programId = useSelector(selectSelectedProgram).id
  const purseId = useOutletContext<PurseDetailOutletContext>().purse.id
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(PasswordFormSchema),
    mode: 'onBlur',
  })

  const { toast } = useToast()

  const downloadMutation = useDownloadDistributionReport({
    onSuccess: (blob, distributionId) => {
      // Use the blob directly - wrapping it in a new Blob can corrupt binary ZIP data
      // The backend should set the correct Content-Type header
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `distribution-report-${distributionId}-${new Date().toISOString().split('T')[0]}.zip`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      onOpenChange(false)
      toast({
        title: 'Distribution report downloaded successfully',
        variant: 'default',
      })
    },
    onError: (error) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'Failed to download distribution report',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })

  const handleDialogClose = (isOpen: boolean) => {
    if (!isOpen && isPending) {
      return // Prevent closing when mutation is pending
    }
    onOpenChange(isOpen)
  }

  const onSubmit = (data: PasswordFormData) => {
    downloadMutation.mutate({
      programId: programId,
      purseId: purseId,
      distributionId: distribution.distribution_id,
      password: data.password,
    })
  }

  const isPending = downloadMutation.status === 'pending'

  return (
    <Dialog open={!!distribution} onOpenChange={handleDialogClose}>
      <DialogContent
        onInteractOutside={(e) => {
          if (isPending) {
            e.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Password Protect Distribution Report</DialogTitle>
          <DialogDescription className="text-left">
            Enter a password to protect the distribution report file. This
            password will be required to open the downloaded file, ensuring that
            sensitive student distribution data remains secure. Please remember
            this password as it cannot be recovered.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label
                htmlFor="password"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Password <span className="text-red-500">*</span>
              </label>
              <Input
                id="password"
                type="password"
                {...register('password')}
                placeholder="Enter password"
                autoComplete="new-password"
                disabled={isPending}
              />
              <FormErrorMessage message={errors.password?.message} />
            </div>
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDialogClose(false)}
              className="w-full sm:w-auto"
              disabled={isPending}
            >
              Cancel
            </Button>
            <SubmitButton
              type="submit"
              variant="primary"
              className="w-full sm:w-auto"
              status={downloadMutation.status}
              buttonTxt="Download"
              submittingText="Downloading..."
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
